/**
 * LoadingStates.js - Manages game loading states and transitions
 * 
 * This module defines the different loading states for the game and provides
 * functions to manage transitions between them.
 * 
 * States:
 * - NOT_STARTED: Initial state, nothing loaded
 * - MINIMAL_LOADED: Minimum playable chunks loaded (1-2 chunks around spawn)
 * - PLAYABLE: Enough terrain loaded for basic gameplay
 * - COMPLETE: All initial chunks loaded
 */

// Loading state constants
export const LOADING_STATES = {
    NOT_STARTED: 'not_started',
    MINIMAL_LOADED: 'minimal_loaded',
    PLAYABLE: 'playable',
    COMPLETE: 'complete'
};

// Default loading settings
export const DEFAULT_LOADING_CONFIG = {
    // Chunk loading settings - increased for Minecraft-like experience
    minimalChunksRadius: 2,  // Initial radius for immediate playability
    playableChunksRadius: 4, // Radius for playable state (8x8 grid)
    completeChunksRadius: 6, // Full radius for complete state (12x12 grid)
    
    // Performance settings
    initialFogDensity: 0.08, // Increased fog during initial load to hide popping
    playableFogDensity: 0.04, // Medium fog when playable
    completeFogDensity: 0.01, // Light fog when complete
    
    // Loading timing
    loadingDelay: 50,        // Minimal delay to prevent UI thread blocking
};

/**
 * Create a new loading state tracker
 * @param {Function} onStateChange - Callback when state changes
 * @returns {Object} Loading state interface
 */
export function createLoadingStateTracker(onStateChange = null) {
    let currentState = LOADING_STATES.NOT_STARTED;
    let loadingProgress = {
        chunks: {
            loaded: 0,
            total: 0
        }
    };
    
    /**
     * Update the current loading state
     * @param {string} newState - New loading state
     */
    function updateState(newState) {
        if (newState !== currentState) {
            const prevState = currentState;
            currentState = newState;
            console.log(`[Loading] State changed: ${prevState} -> ${currentState}`);
            
            if (onStateChange) {
                onStateChange(currentState, prevState);
            }
        }
    }
    
    /**
     * Update loading progress
     * @param {number} loaded - Number of loaded chunks
     * @param {number} total - Total chunks to load
     */
    function updateProgress(loaded, total) {
        loadingProgress.chunks.loaded = loaded;
        loadingProgress.chunks.total = total;
    }
    
    return {
        state: currentState,
        progress: loadingProgress,
        updateState,
        updateProgress
    };
} 